import { RpcError, RpcStatus } from '@protobuf-ts/runtime-rpc'
import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'
import { debounce } from 'components/no-ui/helpers/debounce'
import { getNotifyTokens, NotifyClient } from 'lib/notify'
import { AllowedRefs, WebsocketNotifyClient, convertToRefString, getRefString } from 'lib/websocket-notify'
import { websocketFilterManager } from 'lib/ws/services/websocket-filter-manager'
import { ChangedRefEventType } from 'pbts/c1/api/websocketnotify/v1/websocketnotify'
import { Timestamp } from 'pbts/google/protobuf/timestamp'
import { rpcNotReadyErr } from 'redux_toolkit/lib/createPagedListApi'
import { ACTIONS, ERRORED, INVALIDATE_TAG, LOADED, LOADING, SUBSCRIBE_OBJECT, UNSUBSCRIBE } from './actionTypes'
import type { InvokeObjectRPC } from './createUnaryApi'
import { ReduxObjectState, ReduxObjectCache, SerializedErrorType, serializedError } from './reducer'
import { OBJECT_SUBSCRIPTIONS } from './subscriptions'
import { compositeKey } from './utils'

const equal = (x: Set<string>, y: Set<string>): boolean => x.size === y.size && [...x].every((v) => y.has(v))

export type Flags<Response> = {
    notify?: WebsocketNotifyClient
    deprecatedNotify?: NotifyClient
    maxRetries?: number
    evictionAfterSeconds?: number
    transformResponse?: (r: Response) => unknown
    tag?: string
    actionable?: boolean
    force?: boolean
}

export class GRPCSubcription<Request extends object, Response extends object> {
    private notifyAborter: Record<string, AbortController>
    private aborter?: AbortController // equivalent to a request in flight
    private retryTimer?: number // retrying reqs on error
    private notifyRetryTimer?: number // retrying reqs on error
    private destructionTimer?: number // destructor on the GRPCSubcription
    private deNotify?: () => void // unsubscribe from notificaitons
    private ref_count = 0
    private deprecatedNotify?: NotifyClient
    private notify?: WebsocketNotifyClient
    private transformResponse?: (r: Response) => unknown
    private maxRetries: number
    private evictionAfterSeconds: number
    private cacheInvalidationTime = -Infinity
    private notifyTokens: Set<string> = new Set()
    public tag?: string

    constructor(
        private dispatch_: ThunkDispatch<ReduxObjectState<object>, never, UnknownAction>,
        private _getState: () => ReduxObjectState<object & { updatedAt?: Timestamp }>,
        public reducer: string,
        public cacheKey: string,
        private invokeRPC_: InvokeObjectRPC<Request, Response>,
        opts?: Flags<Response>,
    ) {
        this.transformResponse = opts?.transformResponse
        this.deprecatedNotify = opts?.deprecatedNotify
        this.notifyAborter = {}
        this.notify = opts?.notify
        this.maxRetries = opts?.maxRetries || 3
        const secs = opts?.evictionAfterSeconds !== undefined ? opts?.evictionAfterSeconds : 60
        this.evictionAfterSeconds = secs * 1000
        this.tag = opts?.tag
    }

    get state(): ReduxObjectCache<object & { updatedAt?: Timestamp }> {
        return this._getState().toolkit[this.reducer][this.cacheKey]
    }

    get key(): string {
        return compositeKey(this.reducer, this.cacheKey)
    }

    get alive(): boolean {
        return this.ref_count > 0
    }

    dispatch = ({
        type,
        response,
        error,
        tag,
        refString,
    }: {
        type: ACTIONS
        response?: unknown
        error?: Error
        status?: RpcStatus
        tag?: string
        refString?: string
    }): ReturnType<typeof this.dispatch_> =>
        this.dispatch_({
            type,
            reducer: this.reducer,
            response,
            error,
            cacheKey: this.cacheKey,
            now: Date.now(),
            refString,
            tag,
        })

    subscribe = async (): Promise<unknown> => {
        this.ref_count += 1
        this.dispatch({ type: SUBSCRIBE_OBJECT, tag: this.tag })

        clearInterval(this.destructionTimer)
        delete this.destructionTimer
        const result = this.invokeRPC(false, this.maxRetries)
        GRPCSubcription.notifyFilterManager()
        return result
    }

    unsubscribe = (): boolean => {
        this.ref_count = Math.max(0, this.ref_count - 1)
        if (this.alive || this.destructionTimer) {
            return false
        }

        this.destructionTimer = setTimeout(this.destroy, this.evictionAfterSeconds) as unknown as number
        return true
    }

    invalidate = (): boolean => {
        this.dispatch({
            type: LOADING,
            tag: this.tag,
        })
        this.cacheInvalidationTime = -Infinity
        this.invokeRPC(true, this.maxRetries, 0)
        return true
    }

    getData = (force = false): Promise<unknown> => {
        return this.invokeRPC(force, this.maxRetries)
    }

    invokeRPC = async (force: boolean, availableRetries: number, w = 1): Promise<unknown> => {
        if (!this.alive) {
            return
        }

        if ((Date.now() <= this.cacheInvalidationTime || this.aborter) && !force) {
            return
        }

        // cancel request in flight and start over (useful on notification)
        if (this.aborter) {
            this.aborter.abort()
        }

        // abundance of caution
        clearTimeout(this.retryTimer)
        delete this.retryTimer

        let response: undefined | Awaited<ReturnType<InvokeObjectRPC<Request, Response>>>
        let error: undefined | RpcError

        this.aborter = new AbortController()
        try {
            response = await this.invokeRPC_({ abort: this.aborter.signal })
        } catch (e) {
            // RpcError seems to be enforced with words, not the type system :-/
            error = (e || new Error('!?!')) as unknown as RpcError
        }

        delete this.aborter

        // TODO: should we really throw away the response if we aren't alive?
        if (!this.alive || error?.code === 'CANCELLED') {
            // subscription ended, or we aborted the call (handled on the other stack frame)
            return
        }

        if (error) {
            if (error === rpcNotReadyErr) {
                return
            }
            this.dispatch({ type: ERRORED, error: serializedError(error) })
            if (availableRetries >= 1) {
                const timeout = 1000 * Math.min(w, 120)
                this.retryTimer = setTimeout(
                    () => this.invokeRPC(force, availableRetries - 1, w * 2),
                    timeout,
                ) as unknown as number
            }
            return
        }

        // Make TS happy
        if (!response) {
            throw new Error('no response!?')
        }

        // response will always have code OK here....
        const notifyTokens = new Set(getNotifyTokens(response.headers || {}))
        if (this.deNotify && !equal(notifyTokens, this.notifyTokens)) {
            this.deNotify()
            delete this.deNotify
        }

        this.notifyTokens = notifyTokens

        if (this.deprecatedNotify) {
            this.deNotify = notifyTokens.size
                ? this.deprecatedNotify.subscribe([...notifyTokens], () => this.invokeRPC(true, this.maxRetries))
                : undefined
        }

        if (this.notify && this.tag) {
            this.notify.subscribe(
                [this.tag],
                this.cacheKey,
                (ref: AllowedRefs, eventType: ChangedRefEventType, lastUpdatedAt: Timestamp | undefined) => {
                    this.onNotifyDebounced(true, 2, 1, ref, eventType, lastUpdatedAt)
                },
            )
        }

        this.cacheInvalidationTime = Date.now() + this.evictionAfterSeconds

        const result = this.dispatch({
            type: LOADED,
            status: response.status,
            response: this.transformResponse ? this.transformResponse(response.response) : response.response,
        })

        GRPCSubcription.notifyFilterManager()
        return result
    }

    onNotify = async (
        force: boolean,
        availableRetries: number,
        wait = 1,
        ref: AllowedRefs,
        eventType: ChangedRefEventType,
        lastUpdatedAt: Timestamp | undefined,
    ): Promise<unknown> => {
        if (!this.alive || !ref || eventType === ChangedRefEventType.UNSPECIFIED || !this.tag) {
            return
        }

        const refString = getRefString(ref)
        const { data: cached } = this.state

        if (!cached) {
            return
        }

        const refStringFromCache = convertToRefString(cached, this.tag)

        if (refStringFromCache !== refString) {
            console.warn("OBJECT: tag doesn't match ref of the item to update", { refString, refStringFromCache, cached })
            return
        }

        if (lastUpdatedAt && cached.updatedAt) {
            try {
                const lastUpdatedTimeFromNotify = lastUpdatedAt.seconds * 1000 + lastUpdatedAt.nanos / 1000000
                const lastUpdatedTimeInCache = cached.updatedAt.seconds * 1000 + cached.updatedAt.nanos / 1000000
                if (lastUpdatedTimeInCache >= lastUpdatedTimeFromNotify) {
                    return
                }
            } catch (e) {
                console.warn('Error comparing time updating from notify', lastUpdatedAt)
            }
        }

        if (this.notifyAborter[refString]) {
            if (!force) {
                return
            }
            // cancel request in flight and start over (useful on notification)
            // does this make sense for lists though?
            // how do we know it's for this page??
            this.notifyAborter[refString].abort()
        }

        // abundance of caution
        clearTimeout(this.notifyRetryTimer)
        delete this.notifyRetryTimer

        let response: undefined | Awaited<ReturnType<InvokeObjectRPC<Request, Response>>>
        let error: undefined | RpcError

        this.notifyAborter[refString] = new AbortController()
        try {
            response = await this.invokeRPC_({ abort: this.notifyAborter[refString].signal })
        } catch (e) {
            return
        }

        delete this.notifyAborter[refString]

        if (!this.alive || error?.code === 'CANCELLED') {
            // subscription ended, or we aborted the call (handled on the other stack frame)
            return
        }

        if (error) {
            const errorSerialized: SerializedErrorType = {
                message: error.message,
                name: error.name,
                ...(error.code && { code: error.code }),
            }
            this.dispatch({ type: ERRORED, error: errorSerialized })
            if (availableRetries >= 1) {
                this.notifyRetryTimer = setTimeout(
                    this.onNotify,
                    wait * 1000,
                    force,
                    availableRetries - 1,
                    Math.min(wait * 2, 120),
                ) as unknown as number
            }
            return
        }

        if (!response) {
            return
        }

        const resp = this.transformResponse ? this.transformResponse(response.response) : response.response

        return this.invalidateTag(refString, resp)
    }

    onNotifyDebounced = debounce(this.onNotify)

    destroy = (): void => {
        if (this.alive) {
            console.warn('tried to destroy an live subscription', this.key, this.ref_count)
            return
        }

        if (this.deNotify) {
            this.deNotify()
            delete this.deNotify
        }

        if (this.aborter) {
            this.aborter.abort()
            delete this.aborter
        }

        for (const aborter of Object.values(this.notifyAborter)) {
            aborter.abort()
        }

        this.notifyAborter = {}

        clearTimeout(this.destructionTimer)
        clearTimeout(this.retryTimer)
        clearTimeout(this.notifyRetryTimer)

        delete this.retryTimer
        delete this.destructionTimer
        delete OBJECT_SUBSCRIPTIONS[this.key]

        this.dispatch({
            type: UNSUBSCRIBE,
        })

        GRPCSubcription.notifyFilterManager()
    }

    invalidateTag(refString: string, response: unknown): void {
        this.dispatch({
            type: INVALIDATE_TAG,
            response,
            refString,
        })
    }

    private static notifyFilterManager(): void {
        websocketFilterManager.onSubscriptionsChanged()
    }
}
